import React from 'react';
import { MetricCard } from '../ui/metric-card';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { ResumenDelFiltro } from '../../utils/resumenMulticuenta/agregado';
import { fmtImporteConSigno, fmtPorcentaje } from '../../utils/resumenMulticuenta/formato';
import { DESDE_EL_INICIO, PeriodoEvolucion } from '../../utils/resumenMulticuenta/evolucion';

export interface MetricasResumenProps {
	resumen: ResumenDelFiltro;
	/** El periodo del selector de año de arriba: `'todo'` o un año. */
	periodo: PeriodoEvolucion;
	/** `AAAA` en curso (Madrid): un año anterior enseña lo que había a 31/12. */
	anioEnCurso: string;
	language: string;
	translate: (clave: string) => string;
}

/**
 * Las cifras grandes del Resumen (`HeroMetrics` de Proa): total ahorrado, lo rescatado este año
 * (solo si lo hubo) y la rentabilidad acumulada. Mientras llegan los planes, «Calculando…»; sin
 * dato de algún plan, «Sin dato»: nunca un 0 % ni un total parcial.
 */
const MetricasResumen: React.FC<MetricasResumenProps> = ({
	resumen,
	periodo,
	anioEnCurso,
	language,
	translate
}) => {
	const desdeElInicio = periodo === DESDE_EL_INICIO;
	const t = (clave: string) => translate(`resumen.metrics.${clave}`);
	const conRescates = resumen.rescatadoAnio > 0;
	const valorClase = 'text-lg sm:text-2xl';
	const { rentabilidad } = resumen;
	return (
		<div
			className={conRescates ? 'grid grid-cols-2 gap-3 sm:grid-cols-3' : 'grid grid-cols-2 gap-3'}
		>
			<MetricCard
				label={
					!desdeElInicio && periodo < anioEnCurso
						? t('totalSavedAt').replace('{anio}', periodo)
						: t('totalSaved')
				}
				value={fmtEur(resumen.totalAhorrado)}
				sub={
					resumen.numPlanes === 1
						? t('inOnePlan')
						: t('inPlans').replace('{n}', String(resumen.numPlanes))
				}
				valueClassName={valorClase}
			/>
			{conRescates && (
				<MetricCard
					label={t('rescued')}
					value={fmtEur(resumen.rescatadoAnio)}
					valueClassName={valorClase}
				/>
			)}
			<MetricCard
				label={
					desdeElInicio ? t('profitability') : t('profitabilityYear').replace('{anio}', periodo)
				}
				value={
					rentabilidad
						? fmtImporteConSigno(rentabilidad.ganancia)
						: resumen.cargando
							? t('loading')
							: t('noData')
				}
				sub={
					rentabilidad
						? (desdeElInicio ? t('sinceStart') : t('inYear').replace('{anio}', periodo)).replace(
								'{porcentaje}',
								fmtPorcentaje(rentabilidad.porcentaje, language)
							)
						: undefined
				}
				valueClassName={valorClase}
			/>
		</div>
	);
};

export default MetricasResumen;
